import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BossesHeader } from '../components/bosses/BossesHeader';
import { EmptyState } from '../components/EmptyState';
import { BossMatrixGrid } from '../components/bosses/BossMatrixGrid';
import { BossMatrixMobile } from '../components/bosses/BossMatrixMobile';
import { CharacterTabStrip, type CharacterTabItem } from '../components/bosses/CharacterTabStrip';
import { CharacterBossList } from '../components/bosses/CharacterBossList';
import type { BossMatrixCharacter, BossTierValue } from '../lib/bosses';
import {
  useBossMatrix,
  useDeleteBossPlan,
  useIncome,
  useUpsertBossClear,
  useUpsertBossPlan,
} from '../lib/bosses';
import { dailyKey, monthlyKey, shiftWeek, weeklyKey, formatWeekRange } from '../lib/period';
import { periodKeyForTier as periodKeyForTierShared } from '../lib/bossPeriod';
import { useAccounts } from '../lib/roster';
import { crystalMultiplierForWorldType } from '../lib/bossGrouping';
import { classDisplayName, useBossCatalog, useClasses, useWorlds } from '../lib/reference';
import type { BossDto } from '../lib/reference';

/**
 * `/bosses` — per-character boss lists under a PowerShell-style tab strip (docs/DESIGN.md), plus a
 * trailing "Overview" tab that keeps the original whole-account matrix. A character's boss list
 * *is* its `boss_plan` rows: setting a plan (via the "Add bosses" picker) adds it to the list,
 * deleting the plan removes it — clears history (`boss_clear`) is independent and always kept. See
 * `docs/specs/POST-MVP-fixes.md` §8 for the redesign notes and the superseded hidden-boss feature.
 */
export function Bosses() {
  const now = new Date();
  const todayIso = dailyKey(now);
  const currentWeekKeyIso = weeklyKey(now);
  const currentMonthKeyIso = monthlyKey(now);

  const [weekKey, setWeekKey] = useState(currentWeekKeyIso);
  const [searchParams, setSearchParams] = useSearchParams();
  // `${characterId}:${tier}` while a per-character-list "Complete all" (REV2 §5) is running —
  // separate from `clearPendingKey` since Complete-all fires several clear upserts in sequence,
  // not the single in-flight mutation that key tracks.
  const [completeAllPendingKey, setCompleteAllPendingKey] = useState<string | null>(null);

  const accountsQuery = useAccounts();
  const bossCatalogQuery = useBossCatalog();
  // Read once here and threaded down as a plain number prop (not re-fetched by any child) — worlds
  // is the same long-staleTime reference query Characters.tsx already uses for its Heroic badge, so
  // this is normally a cache hit, not a second network round trip.
  const worldsQuery = useWorlds();
  // REV18: friendly class names in the tab microtext + matrix headers instead of raw slugs.
  const classesQuery = useClasses();
  const classes = classesQuery.data ?? [];
  const accounts = accountsQuery.data ?? [];

  // REV14: account selection lives in `?account=`, not `useState` — mirrors Links.tsx's
  // requested -> valid-set -> first-account fallback exactly, so a cross-account `boss <char>`
  // command-palette jump (`?account=<id>&character=<id>`) resolves `?character=` against the
  // right account's roster instead of silently falling back to the first account.
  const accountParam = searchParams.get('account');
  const requestedAccountId = accountParam !== null ? Number(accountParam) : null;
  const validAccountIds = new Set(accounts.map((a) => a.id));
  const effectiveAccountId =
    requestedAccountId !== null && validAccountIds.has(requestedAccountId)
      ? requestedAccountId
      : (accounts[0]?.id ?? null);

  const selectAccount = (id: number) => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.set('account', String(id));
      return next;
    });
  };

  const matrixQuery = useBossMatrix(effectiveAccountId, weekKey);
  const incomeQuery = useIncome(effectiveAccountId, weekKey);
  const upsertPlan = useUpsertBossPlan();
  const upsertClear = useUpsertBossClear();
  const deletePlan = useDeleteBossPlan();

  // The account's world type, resolved from the roster's worldSlug against the worlds reference
  // catalog — the account's own Heroic-ness, not anything a client should ever compute itself.
  const effectiveAccount = accounts.find((a) => a.id === effectiveAccountId);
  const effectiveWorldType = worldsQuery.data?.find((w) => w.slug === effectiveAccount?.worldSlug)?.worldType;
  const crystalMultiplier = crystalMultiplierForWorldType(effectiveWorldType);

  const isCurrentWeek = weekKey === currentWeekKeyIso;

  /**
   * The single period key each tier's cell operates on. WEEKLY uses the viewed week itself
   * (backfillable to any past week, same as the checklist). MONTHLY prefers the current calendar
   * month when the viewed week overlaps it, else the first month the week overlaps — the same
   * "most relevant of the available keys" call WP5 made for monthly checklist cells (D-API2).
   * DAILY always means *today*: this matrix has no per-day column the way the checklist grid does,
   * so a daily boss row is only actionable while viewing the current week (see `editableForTier`
   * and the WP6 Deviations note on this judgment call).
   */
  const periodKeyForTier = (tier: BossTierValue): string =>
    periodKeyForTierShared(tier, {
      weekKey,
      todayIso,
      currentMonthKeyIso,
      monthlyKeys: matrixQuery.data?.monthlyKeys ?? [currentMonthKeyIso],
    });

  const editableForTier = (tier: BossTierValue) => tier !== 'DAILY' || isCurrentWeek;

  const planPendingKey =
    upsertPlan.isPending && upsertPlan.variables
      ? `${upsertPlan.variables.characterId}:${upsertPlan.variables.bossSlug}`
      : null;
  const clearPendingKey =
    upsertClear.isPending && upsertClear.variables
      ? `${upsertClear.variables.characterId}:${upsertClear.variables.bossSlug}`
      : null;
  const deletePendingKey =
    deletePlan.isPending && deletePlan.variables
      ? `${deletePlan.variables.characterId}:${deletePlan.variables.bossSlug}`
      : null;

  const handleSetDifficulty = (character: BossMatrixCharacter, boss: BossDto, difficulty: string) => {
    if (effectiveAccountId === null) {
      return;
    }
    const chosen = boss.difficulties.find((d) => d.difficulty === difficulty);
    if (!chosen) {
      return;
    }
    const existingPlan = character.plans.find((p) => p.bossSlug === boss.slug);
    // REV2 §4: if the newly-chosen difficulty's partyMax shrinks below the current partySize,
    // clamp down on this same upsert rather than leaving a selected-but-invalid party badge.
    const clampedPartySize = Math.min(existingPlan?.partySize ?? 1, chosen.partyMax ?? 6);
    upsertPlan.mutate({
      accountId: effectiveAccountId,
      weekKey,
      characterId: character.characterId,
      bossSlug: boss.slug,
      bossName: boss.name,
      tier: boss.tier,
      sharedClearGroup: boss.sharedClearGroup,
      difficulty,
      partySize: clampedPartySize,
      levelReq: chosen.levelReq,
      forceType: chosen.forceType,
      forceReq: chosen.forceReq,
      partyMax: chosen.partyMax,
      crystalValue: chosen.crystalValue,
      crystalType: chosen.crystalType,
      crystalVerified: chosen.verified,
    });
  };

  const handleSetCleared = (character: BossMatrixCharacter, boss: BossDto, periodKey: string, cleared: boolean) => {
    if (effectiveAccountId === null) {
      return;
    }
    const plan = character.plans.find((p) => p.bossSlug === boss.slug);
    if (!plan) {
      return;
    }
    // Checking a clear defaults the "sold" toggle on (the common flow, per spec); unchecking a
    // clear removes it outright, so `crystalSold` is moot there.
    upsertClear.mutate({
      accountId: effectiveAccountId,
      weekKey,
      characterId: character.characterId,
      bossSlug: boss.slug,
      bossName: boss.name,
      tier: boss.tier,
      sharedClearGroup: boss.sharedClearGroup,
      difficulty: plan.difficulty,
      crystalValue: plan.crystalValue,
      crystalType: plan.crystalType,
      periodKey,
      cleared,
      crystalSold: cleared,
    });
  };

  const handleSetCrystalSold = (
    character: BossMatrixCharacter,
    boss: BossDto,
    periodKey: string,
    crystalSold: boolean,
  ) => {
    if (effectiveAccountId === null) {
      return;
    }
    const clear = character.clears.find((c) => c.bossSlug === boss.slug && c.periodKey === periodKey);
    if (!clear) {
      return;
    }
    upsertClear.mutate({
      accountId: effectiveAccountId,
      weekKey,
      characterId: character.characterId,
      bossSlug: boss.slug,
      bossName: boss.name,
      tier: boss.tier,
      sharedClearGroup: boss.sharedClearGroup,
      difficulty: clear.difficulty,
      crystalValue: clear.crystalValue,
      crystalType: clear.crystalType,
      periodKey,
      cleared: true,
      crystalSold,
    });
  };

  /**
   * The per-character list's row-half clicks and "Cleared" badge (REV2 §1/§2) — same upsert-clear
   * mutation as `handleSetCleared`, but with an explicit `crystalSold` instead of always mirroring
   * `cleared` (left half / re-clearing = gold sold; right half / the badge's "turn on" = not sold).
   */
  const handleSetClearedWithGold = (
    character: BossMatrixCharacter,
    boss: BossDto,
    periodKey: string,
    cleared: boolean,
    crystalSold: boolean,
  ) => {
    if (effectiveAccountId === null) {
      return;
    }
    const plan = character.plans.find((p) => p.bossSlug === boss.slug);
    if (!plan) {
      return;
    }
    upsertClear.mutate({
      accountId: effectiveAccountId,
      weekKey,
      characterId: character.characterId,
      bossSlug: boss.slug,
      bossName: boss.name,
      tier: boss.tier,
      sharedClearGroup: boss.sharedClearGroup,
      difficulty: plan.difficulty,
      crystalValue: plan.crystalValue,
      crystalType: plan.crystalType,
      periodKey,
      cleared,
      crystalSold,
    });
  };

  /**
   * REV5: the Gollux coin input's commit handler. Always targets *today's* clear — the row it's
   * attached to only renders while cleared, and a daily-tier row is only editable during the
   * current week (see `periodKeyForTier`/`editableForTier`), so `todayIso` is always the right key.
   */
  const handleSetCoinsEarned = (character: BossMatrixCharacter, boss: BossDto, coinsEarned: number) => {
    if (effectiveAccountId === null) {
      return;
    }
    const clear = character.clears.find((c) => c.bossSlug === boss.slug && c.periodKey === todayIso);
    if (!clear) {
      return;
    }
    upsertClear.mutate({
      accountId: effectiveAccountId,
      weekKey,
      characterId: character.characterId,
      bossSlug: boss.slug,
      bossName: boss.name,
      tier: boss.tier,
      sharedClearGroup: boss.sharedClearGroup,
      difficulty: clear.difficulty,
      crystalValue: clear.crystalValue,
      crystalType: clear.crystalType,
      periodKey: todayIso,
      cleared: true,
      crystalSold: clear.crystalSold,
      coinsEarned,
    });
  };

  /** Party-size badges (REV2 §4) — upserts the existing plan unchanged except for `partySize`. */
  const handleSetPartySize = (character: BossMatrixCharacter, boss: BossDto, partySize: number) => {
    if (effectiveAccountId === null) {
      return;
    }
    const plan = character.plans.find((p) => p.bossSlug === boss.slug);
    if (!plan) {
      return;
    }
    upsertPlan.mutate({
      accountId: effectiveAccountId,
      weekKey,
      characterId: character.characterId,
      bossSlug: boss.slug,
      bossName: plan.bossName,
      tier: plan.tier,
      sharedClearGroup: plan.sharedClearGroup,
      difficulty: plan.difficulty,
      partySize,
      levelReq: plan.levelReq,
      forceType: plan.forceType,
      forceReq: plan.forceReq,
      partyMax: plan.partyMax,
      crystalValue: plan.crystalValue,
      crystalType: plan.crystalType,
      crystalVerified: plan.crystalVerified,
    });
  };

  /**
   * Per-tier "Complete all" (REV2 §5): every planned, not-yet-cleared, not-hidden boss in this
   * tier for this character, cleared with gold sold, one upsert-clear mutation at a time. A
   * per-boss failure already toasts + rolls back via the mutation's own `onError` — this loop just
   * has to keep going past it (`mutateAsync`'s rejection is swallowed here, not re-thrown) so one
   * bad boss can't strand the rest of the tier uncleared.
   */
  const handleCompleteAllTier = async (character: BossMatrixCharacter, tier: BossTierValue) => {
    if (effectiveAccountId === null) {
      return;
    }
    const periodKey = periodKeyForTier(tier);
    const targets = character.plans.filter(
      (plan) =>
        plan.tier === tier &&
        !character.hiddenBossSlugs.includes(plan.bossSlug) &&
        !character.clears.some((c) => c.bossSlug === plan.bossSlug && c.periodKey === periodKey),
    );
    if (targets.length === 0) {
      return;
    }
    setCompleteAllPendingKey(`${character.characterId}:${tier}`);
    try {
      for (const plan of targets) {
        try {
          await upsertClear.mutateAsync({
            accountId: effectiveAccountId,
            weekKey,
            characterId: character.characterId,
            bossSlug: plan.bossSlug,
            bossName: plan.bossName,
            tier: plan.tier,
            sharedClearGroup: plan.sharedClearGroup,
            difficulty: plan.difficulty,
            crystalValue: plan.crystalValue,
            crystalType: plan.crystalType,
            periodKey,
            cleared: true,
            crystalSold: true,
          });
        } catch {
          // Already toasted + rolled back by the mutation's own onError — move on to the rest.
        }
      }
    } finally {
      setCompleteAllPendingKey(null);
    }
  };

  const handleDeletePlan = (character: BossMatrixCharacter, boss: BossDto) => {
    if (effectiveAccountId === null) {
      return;
    }
    deletePlan.mutate({
      accountId: effectiveAccountId,
      weekKey,
      characterId: character.characterId,
      bossSlug: boss.slug,
    });
  };

  if (accountsQuery.isPending || bossCatalogQuery.isPending) {
    return <p className="text-sm text-text-muted">Loading the boss matrix…</p>;
  }
  if (accountsQuery.isError || bossCatalogQuery.isError) {
    return (
      <p role="alert" className="text-sm text-danger">
        Could not load the boss matrix. Please retry in a moment.
      </p>
    );
  }

  if (accounts.length === 0) {
    return (
      <EmptyState
        title="No accounts yet"
        note="Add your main under Characters and let's start planning bosses."
        actionTo="/characters"
        actionLabel="Go to Characters"
      />
    );
  }

  const accountOptions = accounts.map((a) => ({ accountId: a.id, label: a.label }));
  const bossCatalog = bossCatalogQuery.data ?? [];
  const characters = matrixQuery.data?.characters ?? [];

  // Tab list = this account's roster (already sort-ordered by the accounts API), plus a trailing
  // Overview tab for the whole-account matrix. Built from the roster, not the boss matrix, so the
  // tab strip itself renders immediately without waiting on `matrixQuery`.
  const rosterCharacters = effectiveAccount?.characters ?? [];
  const tabs: CharacterTabItem[] = [
    ...rosterCharacters.map((c) => ({
      id: c.id,
      label: c.name,
      microtext: `${classDisplayName(c.classSlug, classes)} · Lv.${c.level}`,
      avatarUrl: c.avatarUrl,
      accountId: c.accountId,
    })),
    { id: 'overview' as const, label: 'Overview' },
  ];
  const validTabIds = new Set<number | 'overview'>(tabs.map((t) => t.id));

  const characterParam = searchParams.get('character');
  const requestedTabId: number | 'overview' | null =
    characterParam === 'overview' ? 'overview' : characterParam !== null ? Number(characterParam) : null;
  const selectedTabId: number | 'overview' =
    requestedTabId !== null && validTabIds.has(requestedTabId)
      ? requestedTabId
      : (rosterCharacters[0]?.id ?? 'overview');

  const selectTab = (id: number | 'overview') => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.set('character', String(id));
      return next;
    });
  };

  const selectedCharacter =
    selectedTabId === 'overview'
      ? null
      : (characters.find((c) => c.characterId === selectedTabId) ?? null);

  return (
    <div className="stagger-children space-y-5">
      <BossesHeader
        accounts={accountOptions}
        selectedAccountId={effectiveAccountId}
        onSelectAccount={selectAccount}
        weekLabel={formatWeekRange(weekKey)}
        onPrevWeek={() => setWeekKey((k) => shiftWeek(k, -1))}
        onNextWeek={() => setWeekKey((k) => shiftWeek(k, 1))}
        onThisWeek={() => setWeekKey(currentWeekKeyIso)}
        isCurrentWeek={isCurrentWeek}
        canGoNext={weekKey < currentWeekKeyIso}
        income={incomeQuery.data}
      />

      <CharacterTabStrip tabs={tabs} selectedId={selectedTabId} onSelect={selectTab} />

      {matrixQuery.isPending ? (
        <p className="text-sm text-text-muted">
          Loading {selectedTabId === 'overview' ? 'this week’s matrix' : 'this character’s bosses'}…
        </p>
      ) : matrixQuery.isError ? (
        <p role="alert" className="text-sm text-danger">
          Could not load the boss matrix. Please retry in a moment.
        </p>
      ) : selectedTabId === 'overview' ? (
        <div id="boss-tabpanel-overview" role="tabpanel" aria-labelledby="boss-tab-overview">
          <div role="region" aria-label="Boss matrix (mobile)" className="block md:hidden">
            <BossMatrixMobile
              characters={characters}
              classes={classes}
              bossCatalog={bossCatalog}
              income={incomeQuery.data}
              editableForTier={editableForTier}
              periodKeyForTier={periodKeyForTier}
              planPendingKey={planPendingKey}
              clearPendingKey={clearPendingKey}
              crystalMultiplier={crystalMultiplier}
              onSetDifficulty={handleSetDifficulty}
              onSetCleared={handleSetCleared}
              onSetCrystalSold={handleSetCrystalSold}
            />
          </div>
          <div role="region" aria-label="Boss matrix (grid)" className="hidden md:block">
            <BossMatrixGrid
              characters={characters}
              classes={classes}
              bossCatalog={bossCatalog}
              income={incomeQuery.data}
              editableForTier={editableForTier}
              periodKeyForTier={periodKeyForTier}
              planPendingKey={planPendingKey}
              clearPendingKey={clearPendingKey}
              crystalMultiplier={crystalMultiplier}
              onSetDifficulty={handleSetDifficulty}
              onSetCleared={handleSetCleared}
              onSetCrystalSold={handleSetCrystalSold}
            />
          </div>
        </div>
      ) : selectedCharacter ? (
        <CharacterBossList
          character={selectedCharacter}
          bossCatalog={bossCatalog}
          income={incomeQuery.data}
          editableForTier={editableForTier}
          periodKeyForTier={periodKeyForTier}
          planPendingKey={planPendingKey}
          clearPendingKey={clearPendingKey}
          deletePendingKey={deletePendingKey}
          crystalMultiplier={crystalMultiplier}
          onSetDifficulty={handleSetDifficulty}
          onSetPartySize={handleSetPartySize}
          onSetClearedWithGold={handleSetClearedWithGold}
          onSetCrystalSold={handleSetCrystalSold}
          onSetCoinsEarned={handleSetCoinsEarned}
          onDeletePlan={handleDeletePlan}
          onCompleteAllTier={handleCompleteAllTier}
          completeAllPendingKey={completeAllPendingKey}
        />
      ) : null}
    </div>
  );
}
